import ArrowDown from "@redotech/redo-icons/arrow-down.svg";
import ArrowUp from "@redotech/redo-icons/arrow-up.svg";
import CheckIcon from "@redotech/redo-icons/check.svg";
import ChevronDown from "@redotech/redo-icons/chevron-down.svg";
import ChevronLeftDouble from "@redotech/redo-icons/chevron-left-double.svg";
import ChevronLeft from "@redotech/redo-icons/chevron-left.svg";
import ChevronRightDouble from "@redotech/redo-icons/chevron-right-double.svg";
import ChevronRight from "@redotech/redo-icons/chevron-right.svg";
import ChevronsUpDown from "@redotech/redo-icons/chevron-selector-vertical.svg";
import ChevronUp from "@redotech/redo-icons/chevron-up.svg";
import DotsGrid from "@redotech/redo-icons/dots-grid.svg";
import EyeOff from "@redotech/redo-icons/eye-off_filled.svg";
import PlusCircleIcon from "@redotech/redo-icons/plus-circle.svg";
import Settings2 from "@redotech/redo-icons/settings-02.svg";
import XCloseIcon from "@redotech/redo-icons/x-close.svg";
import {
  Column,
  type ColumnDef,
  type ColumnFiltersState,
  type ColumnPinningState,
  flexRender,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  Header,
  Row,
  type RowSelectionState,
  Table as TanStackTable,
  useReactTable,
  type VisibilityState,
} from "@tanstack/react-table";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDebounce } from "usehooks-ts";
import { RedoSpinner } from "../../arbiter-components/loading/redo-spinner";
import { cn } from "../../tailwind-utils";
import { Badge } from "./badge";
import { Button } from "./button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "./command";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./dropdown-menu";
import { Input } from "./input";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./select";
import { Separator } from "./separator";
import { Skeleton } from "./skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./table";

/**
 * Context for accessing the table instance
 */
const EnhancedDataTableContext = createContext<TanStackTable<unknown> | null>(
  null,
);

/**
 * Hook to access the table instance from context
 */
export function useEnhancedDataTable<TData>(): TanStackTable<TData> {
  const table = useContext(EnhancedDataTableContext);
  if (!table) {
    throw new Error(
      "useEnhancedDataTable must be used within an EnhancedDataTable component",
    );
  }
  return table as TanStackTable<TData>;
}

/**
 * Props for the enhanced data table
 */
interface EnhancedDataTableProps<TData> {
  /** The data array to display in the table */
  data: TData[];
  /** Column definitions for the table */
  columns: ColumnDef<TData, unknown>[];
  /** Function to get a unique row ID from each data item */
  getRowId: (row: TData) => string;
  className?: string;
  children?: React.ReactNode;
  loading?: boolean;
  onRowClick?: ({ row }: { row: TData }) => void;
  maxHeight?: string;
  getRowProps?: (
    row: Row<TData>,
  ) => React.HTMLAttributes<HTMLTableRowElement> & {
    [key: `data-${string}`]: string | number | boolean;
  };
  enableColumnResizing?: boolean;
  enableColumnPinning?: boolean;
  enableRowPinning?: boolean;
  stickyPinnedRows?: boolean;
  enableInfiniteScroll?: boolean;
  onLoadMore?: () => void;
  hasMore?: boolean;
  loadingMore?: boolean;
}

/**
 * Enhanced Data Table component with support for:
 * - Column ordering (drag and drop)
 * - Column pinning (left/right)
 * - Column sizing (resizable)
 * - Column visibility
 * - Row pinning (top/bottom)
 * - Global filtering
 * - Pagination
 * - Sorting
 */
export function EnhancedDataTable<TData>({
  data,
  columns,
  getRowId,
  className,
  children,
  loading = false,
  onRowClick,
  maxHeight,
  getRowProps,
  enableColumnResizing = false,
  enableColumnPinning = false,
  enableRowPinning = false,
  stickyPinnedRows = true,
  enableInfiniteScroll = false,
  onLoadMore,
  hasMore = false,
  loadingMore = false,
}: EnhancedDataTableProps<TData>) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Table state managed internally
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [globalFilter, setGlobalFilter] = useState("");
  const [columnPinning, setColumnPinning] = useState<ColumnPinningState>({
    left: [],
    right: [],
  });

  // Memoize columns to prevent unnecessary re-renders
  const memoizedColumns = useMemo(() => columns, [columns]);

  // Create table instance internally - this ensures proper reactivity to data changes
  const table = useReactTable({
    data,
    columns: memoizedColumns,
    state: {
      columnFilters,
      columnVisibility,
      rowSelection,
      globalFilter,
      columnPinning,
    },
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    onGlobalFilterChange: setGlobalFilter,
    onColumnPinningChange: setColumnPinning,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getRowId,
    manualPagination: true,
    manualSorting: true,
    enableColumnPinning,
  });

  const childrenArray = React.Children.toArray(children);
  const toolbar = childrenArray.find(
    (child) =>
      React.isValidElement(child) && child.type === EnhancedDataTableToolbar,
  );
  const pagination = childrenArray.find(
    (child) =>
      React.isValidElement(child) && child.type === EnhancedDataTablePagination,
  );

  const rowCount = table.getRowModel().rows.length;

  useEffect(() => {
    if (
      !enableInfiniteScroll ||
      !onLoadMore ||
      !hasMore ||
      loadingMore ||
      loading
    ) {
      return;
    }

    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) {
      return;
    }

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
      // Trigger load more when user is within 200px of bottom
      if (distanceFromBottom < 200) {
        onLoadMore?.();
      }
    };

    // Check immediately in case content doesn't fill the container
    handleScroll();

    scrollContainer.addEventListener("scroll", handleScroll, { passive: true });
    return () => scrollContainer.removeEventListener("scroll", handleScroll);
  }, [
    enableInfiniteScroll,
    onLoadMore,
    hasMore,
    loadingMore,
    loading,
    rowCount,
  ]);

  const renderPinnedRows = (
    position: "top" | "bottom",
    rows: Row<TData>[],
  ): React.ReactNode => {
    if (!enableRowPinning || rows.length === 0) return null;

    return rows.map((row) => {
      const customRowProps = getRowProps?.(row) ?? {};
      const { className: customClassName, ...otherCustomProps } =
        customRowProps;

      return (
        <TableRow
          {...otherCustomProps}
          className={cn(
            onRowClick
              ? "cursor-pointer hover:bg-muted focus:bg-muted outline-none"
              : undefined,
            stickyPinnedRows
              ? position === "top"
                ? "sticky top-[41px] z-[5] bg-muted/80"
                : "sticky bottom-0 z-[5] bg-muted/80"
              : "bg-muted/50",
            customClassName,
          )}
          data-pinned={position}
          data-state={row.getIsSelected() && "selected"}
          key={row.id}
          onClick={
            onRowClick ? () => onRowClick({ row: row.original }) : undefined
          }
          onKeyDown={
            onRowClick
              ? (e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onRowClick({ row: row.original });
                  }
                }
              : undefined
          }
          tabIndex={onRowClick ? 0 : undefined}
        >
          {row.getVisibleCells().map((cell) => (
            <TableCell
              className={cn(
                cell.column.getIsPinned() === "left" &&
                  "sticky left-0 z-[1] bg-inherit",
                cell.column.getIsPinned() === "right" &&
                  "sticky right-0 z-[1] bg-inherit",
              )}
              key={cell.id}
              style={{ width: cell.column.getSize() }}
            >
              {flexRender(cell.column.columnDef.cell, cell.getContext())}
            </TableCell>
          ))}
        </TableRow>
      );
    });
  };

  return (
    <EnhancedDataTableContext.Provider value={table as TanStackTable<unknown>}>
      <div className={cn("space-y-4", className)}>
        {toolbar && <div>{toolbar}</div>}
        {loading ? (
          <EnhancedDataTableSkeleton
            className={className}
            columnCount={table.getAllColumns().length}
            filterCount={0}
            maxHeight={maxHeight}
            rowCount={table.getState().pagination?.pageSize ?? 10}
            withPagination={false}
            withViewOptions={false}
          />
        ) : (
          <div
            className={cn(
              "rounded-md border bg-background",
              maxHeight ? "relative overflow-auto" : "",
            )}
            ref={scrollContainerRef}
            style={maxHeight ? { maxHeight, height: maxHeight } : undefined}
          >
            <Table noWrapper={!!maxHeight}>
              <TableHeader
                className={
                  maxHeight ? "bg-background sticky top-0 z-10" : undefined
                }
              >
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => {
                      const isPinnedLeft =
                        header.column.getIsPinned() === "left";
                      const isPinnedRight =
                        header.column.getIsPinned() === "right";

                      return (
                        <TableHead
                          className={cn(
                            isPinnedLeft && "sticky left-0 z-[2] bg-background",
                            isPinnedRight &&
                              "sticky right-0 z-[2] bg-background",
                          )}
                          colSpan={header.colSpan}
                          key={header.id}
                          style={{
                            width: header.getSize(),
                            position: enableColumnResizing
                              ? "relative"
                              : undefined,
                          }}
                        >
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext(),
                              )}
                          {enableColumnResizing &&
                            header.column.getCanResize() && (
                              <ColumnResizer header={header} />
                            )}
                        </TableHead>
                      );
                    })}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {enableRowPinning &&
                  renderPinnedRows("top", table.getTopRows())}
                {table.getRowModel().rows?.length ? (
                  (enableRowPinning
                    ? table.getCenterRows()
                    : table.getRowModel().rows
                  ).map((row) => {
                    const customRowProps = getRowProps?.(row) ?? {};
                    const { className: customClassName, ...otherCustomProps } =
                      customRowProps;

                    return (
                      <TableRow
                        {...otherCustomProps}
                        className={cn(
                          onRowClick
                            ? "cursor-pointer hover:bg-muted focus:bg-muted outline-none"
                            : undefined,
                          customClassName,
                        )}
                        data-state={row.getIsSelected() && "selected"}
                        key={row.id}
                        onClick={
                          onRowClick
                            ? () => onRowClick({ row: row.original })
                            : undefined
                        }
                        onKeyDown={
                          onRowClick
                            ? (e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  onRowClick({ row: row.original });
                                }
                              }
                            : undefined
                        }
                        tabIndex={onRowClick ? 0 : undefined}
                      >
                        {row.getVisibleCells().map((cell) => {
                          const isPinnedLeft =
                            cell.column.getIsPinned() === "left";
                          const isPinnedRight =
                            cell.column.getIsPinned() === "right";

                          return (
                            <TableCell
                              className={cn(
                                isPinnedLeft &&
                                  "sticky left-0 z-[1] bg-background",
                                isPinnedRight &&
                                  "sticky right-0 z-[1] bg-background",
                              )}
                              key={cell.id}
                              style={{ width: cell.column.getSize() }}
                            >
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext(),
                              )}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell
                      className="h-24 text-center"
                      colSpan={table.getAllColumns().length}
                    >
                      No results.
                    </TableCell>
                  </TableRow>
                )}
                {enableRowPinning &&
                  renderPinnedRows("bottom", table.getBottomRows())}
                {enableInfiniteScroll && (hasMore || loadingMore) && (
                  <TableRow>
                    <TableCell
                      className="h-16 text-center text-muted-foreground"
                      colSpan={table.getAllColumns().length}
                    >
                      {loadingMore && (
                        <div className="flex items-center justify-center">
                          <RedoSpinner size="sm" />
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        )}
        {!enableInfiniteScroll && pagination && <div>{pagination}</div>}
      </div>
    </EnhancedDataTableContext.Provider>
  );
}

/**
 * Column resizer component for resizable columns
 */
interface ColumnResizerProps<TData, TValue> {
  header: Header<TData, TValue>;
}

function ColumnResizer<TData, TValue>({
  header,
}: ColumnResizerProps<TData, TValue>) {
  return (
    <div
      className={cn(
        "absolute right-0 top-0 h-full w-1 cursor-col-resize select-none touch-none",
        "hover:bg-primary/50",
        header.column.getIsResizing() && "bg-primary",
      )}
      onDoubleClick={() => header.column.resetSize()}
      onMouseDown={header.getResizeHandler()}
      onTouchStart={header.getResizeHandler()}
    />
  );
}

/**
 * Enhanced column header with sorting, pinning, and visibility controls
 */
interface EnhancedDataTableColumnHeaderProps<TData, TValue>
  extends React.HTMLAttributes<HTMLDivElement> {
  column: Column<TData, TValue>;
  title: string;
  enablePinning?: boolean;
}

export function EnhancedDataTableColumnHeader<TData, TValue>({
  column,
  title,
  className,
  enablePinning = false,
}: EnhancedDataTableColumnHeaderProps<TData, TValue>) {
  if (!column.getCanSort() && !column.getCanHide() && !enablePinning) {
    return <div className={cn(className)}>{title}</div>;
  }

  return (
    <div className={cn("flex items-center space-x-2", className)}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            className="-ml-3 h-8 data-[state=open]:bg-accent"
            size="sm"
            variant="ghost"
          >
            <span>{title}</span>
            {column.getIsPinned() && (
              <DotsGrid className="ml-1 h-3 w-3 text-primary" />
            )}
            {column.getCanSort() &&
              (column.getIsSorted() === "desc" ? (
                <ChevronDown />
              ) : column.getIsSorted() === "asc" ? (
                <ChevronUp />
              ) : (
                <ChevronsUpDown />
              ))}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          {column.getCanSort() && (
            <>
              <DropdownMenuItem onClick={() => column.toggleSorting(false)}>
                <ChevronUp className="h-3.5 w-3.5 text-muted-foreground/70" />
                Asc
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => column.toggleSorting(true)}>
                <ChevronDown className="h-3.5 w-3.5 text-muted-foreground/70" />
                Desc
              </DropdownMenuItem>
              <DropdownMenuSeparator />
            </>
          )}
          {enablePinning && column.getCanPin() && (
            <>
              <DropdownMenuLabel className="text-xs">
                Pin column
              </DropdownMenuLabel>
              <DropdownMenuItem
                disabled={column.getIsPinned() === "left"}
                onClick={() => column.pin("left")}
              >
                <ArrowUp className="h-3.5 w-3.5 -rotate-90 text-muted-foreground/70" />
                Pin left
              </DropdownMenuItem>
              <DropdownMenuItem
                disabled={column.getIsPinned() === "right"}
                onClick={() => column.pin("right")}
              >
                <ArrowDown className="h-3.5 w-3.5 -rotate-90 text-muted-foreground/70" />
                Pin right
              </DropdownMenuItem>
              {column.getIsPinned() && (
                <DropdownMenuItem onClick={() => column.pin(false)}>
                  <XCloseIcon className="h-3.5 w-3.5 text-muted-foreground/70" />
                  Unpin
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
            </>
          )}
          {column.getCanHide() && (
            <DropdownMenuItem onClick={() => column.toggleVisibility(false)}>
              <EyeOff className="h-3.5 w-3.5 text-muted-foreground/70" />
              Hide
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

/**
 * Row pin cell component
 */
interface RowPinCellProps<TData> {
  row: Row<TData>;
}

export function RowPinCell<TData>({ row }: RowPinCellProps<TData>) {
  const isPinned = row.getIsPinned();

  if (isPinned) {
    return (
      <Button
        className="h-6 w-6 p-0"
        onClick={(e) => {
          e.stopPropagation();
          row.pin(false);
        }}
        size="sm"
        variant="ghost"
      >
        <XCloseIcon className="h-3 w-3" />
      </Button>
    );
  }

  return (
    <div className="flex gap-1">
      <Button
        className="h-6 w-6 p-0"
        onClick={(e) => {
          e.stopPropagation();
          row.pin("top");
        }}
        size="sm"
        title="Pin to top"
        variant="ghost"
      >
        <ArrowUp className="h-3 w-3" />
      </Button>
      <Button
        className="h-6 w-6 p-0"
        onClick={(e) => {
          e.stopPropagation();
          row.pin("bottom");
        }}
        size="sm"
        title="Pin to bottom"
        variant="ghost"
      >
        <ArrowDown className="h-3 w-3" />
      </Button>
    </div>
  );
}

/**
 * Pagination component
 */
interface EnhancedDataTablePaginationProps<TData> {
  table?: TanStackTable<TData>;
  firstAndLastPageControls?: boolean;
  pageSizeOptions?: number[];
}

export function EnhancedDataTablePagination<TData>({
  table: tableProp,
  firstAndLastPageControls = true,
  pageSizeOptions = [10, 20, 30, 40, 50, 100, 200, 500],
}: EnhancedDataTablePaginationProps<TData>) {
  const contextTable = useContext(EnhancedDataTableContext);
  const table = (tableProp ?? contextTable) as TanStackTable<TData> | null;

  if (!table) {
    throw new Error(
      "EnhancedDataTablePagination requires either a table prop or to be used within an EnhancedDataTable component",
    );
  }

  const hasSelectionColumn = table
    .getAllColumns()
    .some((column) => column.id === "select");

  return (
    <div className="flex items-center justify-between px-2">
      <div className="flex-1 text-sm text-muted-foreground whitespace-nowrap">
        {hasSelectionColumn && (
          <>
            {table.getFilteredSelectedRowModel().rows.length} of{" "}
            {table.getFilteredRowModel().rows.length} row(s) selected.
          </>
        )}
      </div>
      <div className="flex items-center space-x-6 lg:space-x-8">
        <div className="flex items-center space-x-2">
          <p className="text-sm font-medium">Rows per page</p>
          <Select
            onValueChange={(value: string) => {
              table.setPageSize(Number(value));
            }}
            value={`${table.getState().pagination?.pageSize ?? 10}`}
          >
            <SelectTrigger className="h-8 w-[70px]">
              <SelectValue
                placeholder={table.getState().pagination?.pageSize ?? 10}
              />
            </SelectTrigger>
            <SelectContent side="top">
              {pageSizeOptions.map((pageSize) => (
                <SelectItem key={pageSize} value={`${pageSize}`}>
                  {pageSize}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center justify-center text-sm font-medium whitespace-nowrap">
          Page{" "}
          {((table.getState().pagination?.pageIndex ?? 0) + 1).toLocaleString()}{" "}
          of {table.getPageCount().toLocaleString()}
        </div>
        <div className="flex items-center space-x-2">
          {firstAndLastPageControls && (
            <Button
              className="hidden h-8 w-8 p-0 lg:flex"
              disabled={!table.getCanPreviousPage()}
              onClick={() => table.setPageIndex(0)}
              variant="outline"
            >
              <span className="sr-only">Go to first page</span>
              <ChevronLeftDouble />
            </Button>
          )}
          <Button
            className="h-8 w-8 p-0"
            disabled={!table.getCanPreviousPage()}
            onClick={() => table.previousPage()}
            variant="outline"
          >
            <span className="sr-only">Go to previous page</span>
            <ChevronLeft />
          </Button>
          <Button
            className="h-8 w-8 p-0"
            disabled={!table.getCanNextPage()}
            onClick={() => table.nextPage()}
            variant="outline"
          >
            <span className="sr-only">Go to next page</span>
            <ChevronRight />
          </Button>
          {firstAndLastPageControls && (
            <Button
              className="hidden h-8 w-8 p-0 lg:flex"
              disabled={!table.getCanNextPage()}
              onClick={() => table.setPageIndex(table.getPageCount() - 1)}
              variant="outline"
            >
              <span className="sr-only">Go to last page</span>
              <ChevronRightDouble />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * View options component with column visibility and pinning controls
 */
interface EnhancedDataTableViewOptionsProps<TData> {
  table?: TanStackTable<TData>;
  enableColumnPinning?: boolean;
}

export function EnhancedDataTableViewOptions<TData>({
  table: tableProp,
  enableColumnPinning = false,
}: EnhancedDataTableViewOptionsProps<TData>) {
  const contextTable = useContext(EnhancedDataTableContext);
  const table = (tableProp ?? contextTable) as TanStackTable<TData> | null;

  if (!table) {
    throw new Error(
      "EnhancedDataTableViewOptions requires either a table prop or to be used within an EnhancedDataTable component",
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          className="ml-auto hidden h-8 lg:flex"
          size="sm"
          variant="outline"
        >
          <Settings2 />
          View
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[200px]">
        <DropdownMenuLabel>Toggle columns</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {table
          .getAllColumns()
          .filter(
            (column) =>
              typeof column.accessorFn !== "undefined" && column.getCanHide(),
          )
          .map((column) => {
            const meta = column.columnDef.meta as
              | { label?: string }
              | undefined;
            return (
              <DropdownMenuCheckboxItem
                checked={column.getIsVisible()}
                className="capitalize"
                key={column.id}
                onCheckedChange={(value) => column.toggleVisibility(!!value)}
              >
                <span className="flex-1">{meta?.label ?? column.id}</span>
                {enableColumnPinning && column.getIsPinned() && (
                  <DotsGrid className="ml-2 h-3 w-3 text-primary" />
                )}
              </DropdownMenuCheckboxItem>
            );
          })}
        {enableColumnPinning && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="text-xs"
              onClick={() => table.resetColumnPinning()}
            >
              Reset pinning
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * Search component with debouncing
 */
interface EnhancedDataTableSearchProps<TData> {
  table?: TanStackTable<TData>;
  className?: string;
  debounceMs?: number;
  placeholder?: string;
}

export function EnhancedDataTableSearch<TData>({
  table: tableProp,
  className,
  debounceMs = 300,
  placeholder = "Search...",
  ...props
}: EnhancedDataTableSearchProps<TData> &
  Omit<React.ComponentProps<"input">, "onChange" | "value" | "placeholder">) {
  const contextTable = useContext(EnhancedDataTableContext);
  const table = (tableProp ?? contextTable) as TanStackTable<TData> | null;

  if (!table) {
    throw new Error(
      "EnhancedDataTableSearch requires either a table prop or to be used within an EnhancedDataTable component",
    );
  }

  const [searchValue, setSearchValue] = useState(
    (table.getState().globalFilter as string) ?? "",
  );
  const debouncedSearchValue = useDebounce(searchValue, debounceMs);

  useEffect(() => {
    table.setGlobalFilter(debouncedSearchValue);
  }, [debouncedSearchValue, table]);

  return (
    <Input
      className={cn("h-8 w-[150px] lg:w-[250px]", className)}
      onChange={(event) => setSearchValue(event.target.value)}
      placeholder={placeholder}
      value={searchValue}
      {...props}
    />
  );
}

/**
 * Filter reset button
 */
interface EnhancedDataTableFilterResetProps<TData> {
  table?: TanStackTable<TData>;
}

export function EnhancedDataTableFilterReset<TData>({
  table: tableProp,
}: EnhancedDataTableFilterResetProps<TData>) {
  const contextTable = useContext(EnhancedDataTableContext);
  const table = (tableProp ?? contextTable) as TanStackTable<TData> | null;

  if (!table) {
    throw new Error(
      "EnhancedDataTableFilterReset requires either a table prop or to be used within an EnhancedDataTable component",
    );
  }

  const isFiltered = table.getState().columnFilters.length > 0;

  if (!isFiltered) return null;

  return (
    <Button
      onClick={() => table.resetColumnFilters()}
      size="sm"
      variant="ghost"
    >
      Reset
      <XCloseIcon />
    </Button>
  );
}

/**
 * Toolbar component
 */
interface EnhancedDataTableToolbarProps<TData> {
  table?: TanStackTable<TData>;
  children?: React.ReactNode;
}

export function EnhancedDataTableToolbar<TData>({
  table: tableProp,
  children,
}: EnhancedDataTableToolbarProps<TData>) {
  const contextTable = useContext(EnhancedDataTableContext);
  const table = (tableProp ?? contextTable) as TanStackTable<TData> | null;

  if (!table) {
    throw new Error(
      "EnhancedDataTableToolbar requires either a table prop or to be used within an EnhancedDataTable component",
    );
  }

  return (
    <div className="flex items-center justify-between">
      <div className="flex flex-1 items-center gap-2">{children}</div>
    </div>
  );
}

/**
 * Faceted filter component for multi-select filtering
 */
interface EnhancedDataTableFacetedFilterProps<TData, TValue> {
  column?: Column<TData, TValue>;
  title?: string;
  options: {
    label: string;
    value: string;
    icon?: React.ComponentType<{ className?: string }>;
  }[];
}

export function EnhancedDataTableFacetedFilter<TData, TValue>({
  column,
  title,
  options,
}: EnhancedDataTableFacetedFilterProps<TData, TValue>) {
  const facets = column?.getFacetedUniqueValues();
  const selectedValues = new Set(column?.getFilterValue() as string[]);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button className="h-8 border-dashed" size="sm" variant="outline">
          <PlusCircleIcon />
          {title}
          {selectedValues?.size > 0 && (
            <>
              <Separator className="mx-2 h-4" orientation="vertical" />
              <Badge
                className="rounded-sm px-1 font-normal lg:hidden"
                variant="secondary"
              >
                {selectedValues.size}
              </Badge>
              <div className="hidden gap-1 lg:flex">
                {selectedValues.size > 2 ? (
                  <Badge
                    className="rounded-sm px-1 font-normal"
                    variant="secondary"
                  >
                    {selectedValues.size} selected
                  </Badge>
                ) : (
                  options
                    .filter((option) => selectedValues.has(option.value))
                    .map((option) => (
                      <Badge
                        className="rounded-sm px-1 font-normal"
                        key={option.value}
                        variant="secondary"
                      >
                        {option.label}
                      </Badge>
                    ))
                )}
              </div>
            </>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[200px] p-0">
        <Command>
          <CommandInput placeholder={title} />
          <CommandList>
            <CommandEmpty>No results found.</CommandEmpty>
            <CommandGroup>
              {options.map((option) => {
                const isSelected = selectedValues.has(option.value);
                return (
                  <CommandItem
                    key={option.value}
                    onSelect={() => {
                      if (isSelected) {
                        selectedValues.delete(option.value);
                      } else {
                        selectedValues.add(option.value);
                      }
                      const filterValues = Array.from(selectedValues);
                      column?.setFilterValue(
                        filterValues.length ? filterValues : undefined,
                      );
                    }}
                  >
                    <div
                      className={cn(
                        "flex size-4 items-center justify-center rounded-[4px] border",
                        isSelected
                          ? "bg-primary border-primary text-primary-foreground"
                          : "border-input [&_svg]:invisible",
                      )}
                    >
                      <CheckIcon className="text-primary-foreground size-3.5" />
                    </div>
                    {option.icon && (
                      <option.icon className="text-muted-foreground size-4" />
                    )}
                    <span>{option.label}</span>
                    {facets?.get(option.value) && (
                      <span className="text-muted-foreground ml-auto flex size-4 items-center justify-center font-mono text-xs">
                        {facets.get(option.value)}
                      </span>
                    )}
                  </CommandItem>
                );
              })}
            </CommandGroup>
            {selectedValues.size > 0 && (
              <>
                <CommandSeparator />
                <CommandGroup>
                  <CommandItem
                    className="justify-center text-center"
                    onSelect={() => column?.setFilterValue(undefined)}
                  >
                    Clear filters
                  </CommandItem>
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

/**
 * Column ordering controls
 */
interface EnhancedDataTableColumnOrderProps<TData> {
  table?: TanStackTable<TData>;
}

export function EnhancedDataTableColumnOrder<TData>({
  table: tableProp,
}: EnhancedDataTableColumnOrderProps<TData>) {
  const contextTable = useContext(EnhancedDataTableContext);
  const table = (tableProp ?? contextTable) as TanStackTable<TData> | null;

  if (!table) {
    throw new Error(
      "EnhancedDataTableColumnOrder requires either a table prop or to be used within an EnhancedDataTable component",
    );
  }

  const columns = table.getAllLeafColumns();

  const moveColumn = useCallback(
    (columnId: string, direction: "left" | "right") => {
      const currentOrder = table.getState().columnOrder;
      const columnIds =
        currentOrder.length > 0 ? currentOrder : columns.map((col) => col.id);

      const currentIndex = columnIds.indexOf(columnId);
      if (currentIndex === -1) return;

      const newIndex =
        direction === "left"
          ? Math.max(0, currentIndex - 1)
          : Math.min(columnIds.length - 1, currentIndex + 1);

      if (currentIndex === newIndex) return;

      const newOrder = [...columnIds];
      newOrder.splice(currentIndex, 1);
      newOrder.splice(newIndex, 0, columnId);

      table.setColumnOrder(newOrder);
    },
    [table, columns],
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button className="h-8" size="sm" variant="outline">
          <DotsGrid className="mr-2 h-4 w-4" />
          Columns
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[200px]">
        <DropdownMenuLabel>Column order</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {columns
          .filter((column) => column.getCanHide())
          .map((column, index) => {
            const meta = column.columnDef.meta as
              | { label?: string }
              | undefined;
            return (
              <div
                className="flex items-center justify-between px-2 py-1"
                key={column.id}
              >
                <span className="text-sm truncate flex-1">
                  {meta?.label ?? column.id}
                </span>
                <div className="flex gap-1">
                  <Button
                    className="h-6 w-6 p-0"
                    disabled={index === 0}
                    onClick={() => moveColumn(column.id, "left")}
                    size="sm"
                    variant="ghost"
                  >
                    <ChevronUp className="h-3 w-3" />
                  </Button>
                  <Button
                    className="h-6 w-6 p-0"
                    disabled={
                      index ===
                      columns.filter((col) => col.getCanHide()).length - 1
                    }
                    onClick={() => moveColumn(column.id, "right")}
                    size="sm"
                    variant="ghost"
                  >
                    <ChevronDown className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            );
          })}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          className="text-xs"
          onClick={() => table.resetColumnOrder()}
        >
          Reset order
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * Skeleton loading component
 */
interface EnhancedDataTableSkeletonProps extends React.ComponentProps<"div"> {
  columnCount: number;
  rowCount?: number;
  filterCount?: number;
  cellWidths?: string[];
  withViewOptions?: boolean;
  withPagination?: boolean;
  shrinkZero?: boolean;
  maxHeight?: string;
}

export function EnhancedDataTableSkeleton({
  columnCount,
  rowCount = 10,
  filterCount = 0,
  cellWidths = ["auto"],
  withViewOptions = true,
  withPagination = true,
  shrinkZero = false,
  maxHeight,
  className,
  ...props
}: EnhancedDataTableSkeletonProps) {
  const cozyCellWidths = Array.from(
    { length: columnCount },
    (_, index) => cellWidths[index % cellWidths.length] ?? "auto",
  );

  return (
    <div className={cn("flex w-full flex-col gap-2.5", className)} {...props}>
      {(filterCount > 0 || withViewOptions) && (
        <div className="flex w-full items-center justify-between gap-2 overflow-auto p-1">
          <div className="flex flex-1 items-center gap-2">
            {filterCount > 0
              ? Array.from({ length: filterCount }).map((_, i) => (
                  <Skeleton className="h-7 w-[4.5rem] border-dashed" key={i} />
                ))
              : null}
          </div>
          {withViewOptions ? (
            <Skeleton className="ml-auto hidden h-7 w-[4.5rem] lg:flex" />
          ) : null}
        </div>
      )}
      <div
        className={cn(
          "rounded-md border",
          maxHeight ? "relative overflow-y-auto" : "",
        )}
        style={maxHeight ? { height: maxHeight } : undefined}
      >
        <Table noWrapper={!!maxHeight}>
          <TableHeader
            className={
              maxHeight ? "bg-background sticky top-0 z-10" : undefined
            }
          >
            {Array.from({ length: 1 }).map((_, i) => (
              <TableRow className="hover:bg-transparent" key={i}>
                {Array.from({ length: columnCount }).map((_, j) => (
                  <TableHead
                    key={j}
                    style={{
                      width: cozyCellWidths[j],
                      minWidth: shrinkZero ? cozyCellWidths[j] : "auto",
                    }}
                  >
                    <Skeleton className="h-6 w-full" />
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {Array.from({ length: rowCount }).map((_, i) => (
              <TableRow className="hover:bg-transparent" key={i}>
                {Array.from({ length: columnCount }).map((_, j) => (
                  <TableCell
                    key={j}
                    style={{
                      width: cozyCellWidths[j],
                      minWidth: shrinkZero ? cozyCellWidths[j] : "auto",
                    }}
                  >
                    <Skeleton className="h-6 w-full" />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {withPagination ? (
        <div className="flex w-full items-center justify-between gap-4 overflow-auto p-1 sm:gap-8">
          <Skeleton className="h-7 w-40 shrink-0" />
          <div className="flex items-center gap-4 sm:gap-6 lg:gap-8">
            <div className="flex items-center gap-2">
              <Skeleton className="h-7 w-24" />
              <Skeleton className="h-7 w-[4.5rem]" />
            </div>
            <div className="flex items-center justify-center font-medium text-sm">
              <Skeleton className="h-7 w-20" />
            </div>
            <div className="flex items-center gap-2">
              <Skeleton className="hidden size-7 lg:block" />
              <Skeleton className="size-7" />
              <Skeleton className="size-7" />
              <Skeleton className="hidden size-7 lg:block" />
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
